import flower from './flower';
import smiley from './smiley';
import emblem from './emblem';
import { PRELUDE_STEPS, FIRST_LESSON_STEP, PICK_DESIGN_STEP } from './shared/steps';

/**
 * Every embroidery lesson the guided tutorial can run.
 *
 * Each entry owns its artwork, its pinned processing result, and its own steps
 * — lessons differ in what they teach, not just which picture they use, so
 * there's no shared script to parameterise.
 *
 * To add one, follow `./flower`: export `{ id, design, fixture, steps }`, drop
 * the artwork in `public/assets/`, list it below, then run `npm run
 * tutorial:audio`. The opening steps and the export sequence come from
 * `./shared/steps`, so a new lesson only writes the part in between.
 */
export const TUTORIALS = [flower, smiley, emblem];

export const DEFAULT_TUTORIAL_ID = TUTORIALS[0].id;

export const getTutorial = (id) =>
  TUTORIALS.find((tutorial) => tutorial.id === id) || TUTORIALS[0];

/**
 * Finds the lesson a loaded image belongs to, so processing can use its pinned
 * palette instead of running detection. Returns null for ordinary uploads.
 */
export const getTutorialByFileName = (fileName) =>
  (fileName && TUTORIALS.find((tutorial) => tutorial.design.fileName === fileName)) || null;

/**
 * Practice designs skip Gemini colour detection and auto-retrace so layer
 * order stays the one the walkthrough names ("click layer 3 — the outline").
 */
export const pinTutorialProcessing = (fileName, result) => {
  const tutorial = getTutorialByFileName(fileName);
  if (!tutorial?.fixture?.colors) return result;
  return {
    ...result,
    colors: tutorial.fixture.colors,
    wasUpscaled: false,
    upscaleReason: undefined,
  };
};

/** Cards for the practice-design picker. */
export const TUTORIAL_DESIGNS = TUTORIALS.map((tutorial) => ({
  ...tutorial.design,
  tutorialId: tutorial.id,
}));

/**
 * How long the whole set takes, rounded to something a person would say.
 *
 * Summed from the lessons rather than written down separately, so adding one
 * can't leave the promise on the picker quietly wrong.
 */
export const TUTORIAL_TOTAL_MINUTES =
  Math.round(TUTORIALS.reduce((total, { design }) => total + (design.minutes || 0), 0) / 5) * 5;

/** Full step list for a lesson: the shared opening, then the lesson itself. */
export const getTutorialSteps = (id) => [...PRELUDE_STEPS, ...getTutorial(id).steps];

export { PRELUDE_STEPS, FIRST_LESSON_STEP, PICK_DESIGN_STEP };
